/**
 * Version Mismatch Notification Component
 * 
 * Displays a popup notification in the top-right corner when there's a version mismatch
 * between the frontend and backend API versions.
 * 
 * @author autodata-frontend
 * @date 2025-11-07
 */

import React from 'react';
import { Alert, AlertTitle, IconButton, Slide, Snackbar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  snackbar: {
    top: '20px !important',
    right: '20px !important',
  },
  alert: {
    minWidth: '400px',
    backgroundColor: '#fff3cd',
    color: '#856404',
    border: '1px solid #ffeaa7',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  refreshButton: {
    marginLeft: theme.spacing(1),
    backgroundColor: '#856404',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#6c5228',
    },
  },
}));

interface VersionMismatchNotificationProps {
  open: boolean;
  localVersion: string;
  apiVersion: string;
  onClose: () => void;
}

const VersionMismatchNotification: React.FC<VersionMismatchNotificationProps> = ({
  open,
  localVersion,
  apiVersion,
  onClose,
}) => {
  const { classes } = useStyles();

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      className={classes.snackbar}
      TransitionComponent={Slide}
    >
      <Alert
        severity="warning"
        className={classes.alert}
        action={
          <>
            <IconButton
              size="small"
              aria-label="refresh"
              onClick={handleRefresh}
              className={classes.refreshButton}
              title="Refresh page"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        <AlertTitle>
          <strong>Ny versjon tilgjengelig</strong>
        </AlertTitle>
        <div style={{ fontSize: '0.875rem' }}>
          Din versjon: <strong>{localVersion}</strong>
          <br />
          Ny versjon: <strong>{apiVersion}</strong>
          <br /><br />
          <strong>Trykk CTRL + F5 samtidig</strong> for å laste inn siden på nytt.
        </div>
      </Alert>
    </Snackbar>
  );
};

export default VersionMismatchNotification;
